/**
 * AI 분석 작업 테이블 아이템 형태 정의
 * 서버 응답(AiAnalysisJobDTO)을 테이블에서 사용할 수 있는 형태로 변환하기 위한 구조 정의
 * 
 * 서버 응답 구조 참조:
 * - AiAnalysisJobDTO.java: id, startTime, endTime, siteName, siteId, examineDate,
 *   reportName, reportId, processStatus, targetImageCount, completeImageCount,
 *   jobFailReason, processRate, jobRefSeq, completeTime, resourceType, jobKinds
 */

/**
 * 테이블에서 사용하는 AI 분석 작업 아이템의 필드 구조
 * 각 필드의 역할과 변환 방식을 명시
 */
export const AI_ANALYSIS_JOB_TABLE_ITEM_SCHEMA = Object.freeze({
  id: null,                    // number - AI 분석 작업 ID
  jobRefSeq: null,             // number - AI 서버 실행 번호 (요청 관리 일련번호)
  startTime: null,             // string - AI 분석 시작 시간 (ISO 형식)
  reportName: null,            // string - 보고서명
  siteName: null,              // string - 사이트명
  examineDate: null,           // string - 검사일자 (YYYY-MM-DD 형식)
  processStatus: null,         // string - 진행 상태 (한글 텍스트로 변환됨)
  jobKinds: null,              // string - 작업종류 (한글 라벨로 변환됨)
  processRate: null,            // string - 진행률 (텍스트 형식으로 변환됨)
});

/**
 * 서버 응답 데이터를 테이블 아이템 형태로 변환하는 함수
 * @param {Object} serverItem - 서버에서 받은 AiAnalysisJobDTO 객체
 * @param {Function} getStatusText - 진행 상태를 한글 텍스트로 변환하는 함수
 * @param {Function} formatJobKinds - 작업종류를 한글 라벨로 변환하는 함수
 * @param {Function} calculateProcessRateText - 진행률을 텍스트로 변환하는 함수
 * @returns {Object} 테이블에서 사용할 수 있는 형태의 아이템 객체
 */
export function transformToTableItem(
  serverItem,
  getStatusText,
  formatJobKinds,
  calculateProcessRateText
) {
  if (!serverItem) {
    return null;
  }

  const rawStatus = serverItem?.processStatus
    ? String(serverItem.processStatus).toUpperCase()
    : null;

  return {
    id: serverItem?.id ?? null,
    jobRefSeq: serverItem?.jobRefSeq ?? null,
    startTime: serverItem?.startTime ?? null,
    reportName: serverItem?.reportName ?? null,
    siteName: serverItem?.siteName ?? null,
    examineDate: serverItem?.examineDate ?? null,
    /** 표시용(번역 문자열) */
    processStatus: getStatusText(serverItem?.processStatus),
    /** 삭제·UI 강조용 원본 코드 (BEF, ING, …) */
    processStatusCode: rawStatus,
    jobKinds: formatJobKinds(serverItem?.jobKinds),
    processRate: calculateProcessRateText(
      serverItem?.targetImageCount,
      serverItem?.completeImageCount
    ),
    samplingFlag: serverItem?.samplingFlag ?? false
  };
}

/**
 * 서버 응답 배열을 테이블 아이템 배열로 일괄 변환하는 함수
 * @param {Array<Object>} serverItems - 서버에서 받은 AiAnalysisJobDTO 객체 배열
 * @param {Function} getStatusText - 진행 상태를 한글 텍스트로 변환하는 함수
 * @param {Function} formatJobKinds - 작업종류를 한글 라벨로 변환하는 함수
 * @param {Function} calculateProcessRateText - 진행률을 텍스트로 변환하는 함수
 * @returns {Array<Object>} 테이블에서 사용할 수 있는 형태의 아이템 객체 배열
 */
export function transformToTableItems(
  serverItems,
  getStatusText,
  formatJobKinds,
  calculateProcessRateText
) {
  if (!Array.isArray(serverItems)) {
    return [];
  }

  return serverItems
    .map(item => transformToTableItem(item, getStatusText, formatJobKinds, calculateProcessRateText))
    .filter(item => item !== null);
}

